import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';
import { ToastProvider } from './components/common/Toast';
import SetupWizard from './pages/setup/SetupWizard';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import RoleDashboard from './pages/dashboard/RoleDashboard';
import StudentList from './pages/students/StudentList';
import TodayBirthdays from './pages/students/TodayBirthdays';
import NewAdmission from './pages/students/NewAdmission';
import EditStudent from './pages/students/EditStudent';
import Promotion from './pages/students/Promotion';
import Dropout from './pages/students/Dropout';
import Certificates from './pages/students/Certificates';
import StaffList from './pages/staff/StaffList';
import ClassList from './pages/academic/ClassList';
import SubjectList from './pages/academic/SubjectList';
import Timetable from './pages/academic/Timetable';
import HomeworkList from './pages/academic/HomeworkList';
import HomeworkPage from './pages/homework/HomeworkPage';
import AddHomework from './pages/homework/AddHomework';
import HomeworkReports from './pages/homework/HomeworkReports';
import WeeklyReport from './pages/homework/WeeklyReport';
import StudentAttendance from './pages/attendance/StudentAttendance';
import StaffAttendance from './pages/attendance/StaffAttendance';
import AttendanceReports from './pages/attendance/AttendanceReports';
import StudentLeaveManagement from './pages/attendance/StudentLeaveManagement';
import LessonPlan from './pages/academic/LessonPlan';
import ExamSchedule from './pages/exams/ExamSchedule';
import MarksEntry from './pages/exams/MarksEntry';
import Results from './pages/exams/Results';
import HallTicket from './pages/exams/HallTicket';
import FeeStructure from './pages/fees/FeeStructure';
import FeeCollection from './pages/fees/FeeCollection';
import DueTracking from './pages/fees/DueTracking';
import FeeCollectionReport from './pages/fees/FeeCollectionReport';
import BookCatalog from './pages/library/BookCatalog';
import IssueReturn from './pages/library/IssueReturn';
import RouteList from './pages/transport/RouteList';
import VehicleList from './pages/transport/VehicleList';
import LiveTracking from './pages/transport/LiveTracking';
import PickupPoints from './pages/transport/PickupPoints';
import Announcements from './pages/communication/Announcements';
import WhatsAppMessages from './pages/communication/WhatsAppMessages';
import Overview from './pages/reports/Overview';
import AdmissionEnquiry from './pages/admission/AdmissionEnquiry';
import AdmissionEnquiryList from './pages/admission/AdmissionEnquiryList';
import UserManagement from './pages/settings/UserManagement';
import StudentImport from './pages/settings/StudentImport';
import AcademicSettings from './pages/settings/AcademicSettings';
import TeacherMapping from './pages/settings/TeacherMapping';
import QuestionPapers from './pages/questionpapers/QuestionPapers';
import FAScheduleConfig from './pages/questionpapers/FAScheduleConfig';
import LeaveManagement from './pages/hr/LeaveManagement';
import SalaryStructures from './pages/hr/SalaryStructures';
import SalarySlips from './pages/hr/SalarySlips';
import TeacherAttendance from './pages/hr/TeacherAttendance';
import ChangePasswordFirst from './pages/auth/ChangePasswordFirst';
import WhatsAppInbox from './pages/whatsapp/WhatsAppInbox';
import WhatsAppConversations from './pages/whatsapp/WhatsAppConversations';
import WhatsAppConfig from './pages/whatsapp/WhatsAppConfig';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// ── Setup check: show wizard if app is not yet configured ──────
function AppRoot() {
  const [configured, setConfigured] = useState(null); // null = still checking

  useEffect(() => {
    fetch('/api/setup/status')
      .then(r => r.json())
      .then(d => setConfigured(!!d.configured))
      .catch(() => setConfigured(true)); // if server unreachable, assume configured (show login)
  }, []);

  // Checking setup status
  if (configured === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin" />
      </div>
    );
  }

  // Not configured — show setup wizard (no BrowserRouter or auth needed)
  if (!configured) {
    return <SetupWizard onComplete={() => setConfigured(true)} />;
  }

  // Configured — normal app
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppProvider>
            <AppRoutes />
          </AppProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// ── Protected layout ───────────────────────────────────────────
function ProtectedLayout() {
  const { currentUser, mustChangePassword } = useAuth();
  const { loading } = useApp();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/auth/change-password-first" replace />;
  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
      <div className="w-12 h-12 border-4 border-purple-200 border-t-indigo-600 rounded-full animate-spin" />
      <p className="text-gray-500 text-sm font-medium">Loading data...</p>
    </div>
  );
  return <MainLayout />;
}

function AppRoutes() {
  const { currentUser, mustChangePassword } = useAuth();
  return (
    <Routes>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route
        path="/login"
        element={currentUser ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        path="/auth/change-password-first"
        element={currentUser ? <ChangePasswordFirst /> : <Navigate to="/login" replace />}
      />

      {/* All protected pages under MainLayout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<RoleDashboard />} />

        {/* Admission Enquiries */}
        <Route path="/admission/enquiries" element={<AdmissionEnquiryList />} />
        <Route path="/admission/enquiry" element={<AdmissionEnquiry />} />

        {/* Students */}
        <Route path="/students" element={<StudentList />} />
        <Route path="/students/admission" element={<NewAdmission />} />
        <Route path="/students/edit/:id" element={<EditStudent />} />
        <Route path="/students/allocation" element={<StudentList />} />
        <Route path="/students/promotion" element={<Promotion />} />
        <Route path="/students/certificates" element={<Certificates />} />
        <Route path="/students/dropout" element={<Dropout />} />
        <Route path="/students/birthdays" element={<TodayBirthdays />} />

        {/* Staff */}
        <Route path="/staff" element={<StaffList />} />
        <Route path="/staff/register" element={<StaffList />} />

        {/* Academic */}
        <Route path="/academic/classes" element={<ClassList />} />
        <Route path="/academic/subjects" element={<SubjectList />} />
        <Route path="/academic/timetable" element={<Timetable />} />
        <Route path="/academic/lesson-plans" element={<LessonPlan />} />
        <Route path="/academic/homework" element={<HomeworkList />} />
        <Route path="/homework" element={<HomeworkPage />} />
        <Route path="/homework/add" element={<AddHomework />} />
        <Route path="/homework/reports" element={<HomeworkReports />} />
        <Route path="/homework/weekly-report" element={<WeeklyReport />} />

        {/* Attendance */}
        <Route path="/attendance/students" element={<StudentAttendance />} />
        <Route path="/attendance/staff" element={<StaffAttendance />} />
        <Route path="/attendance/reports" element={<AttendanceReports />} />
        <Route path="/attendance/student-leave" element={<StudentLeaveManagement />} />

        {/* Exams */}
        <Route path="/exams/schedule" element={<ExamSchedule />} />
        <Route path="/exams/marks" element={<MarksEntry />} />
        <Route path="/exams/results" element={<Results />} />
        <Route path="/exams/hall-ticket" element={<HallTicket />} />

        {/* Fees */}
        <Route path="/fees/structure"   element={<FeeStructure />} />
        <Route path="/fees/collection"  element={<FeeCollection />} />
        <Route path="/fees/receipts"    element={<FeeCollection />} />
        <Route path="/fees/dues"        element={<DueTracking />} />
        <Route path="/fees/report"      element={<FeeCollectionReport />} />

        {/* Communication */}
        <Route path="/communication/announcements" element={<Announcements />} />
        <Route path="/communication/whatsapp" element={<WhatsAppMessages />} />

        {/* Library */}
        <Route path="/library/catalog" element={<BookCatalog />} />
        <Route path="/library/issue-return" element={<IssueReturn />} />

        {/* Transport */}
        <Route path="/transport/live" element={<LiveTracking />} />
        <Route path="/transport/routes" element={<RouteList />} />
        <Route path="/transport/vehicles" element={<VehicleList />} />
        <Route path="/transport/pickup-points" element={<PickupPoints />} />

        {/* Question Papers */}
        <Route path="/question-papers" element={<QuestionPapers />} />
        <Route path="/question-papers/fa-schedule" element={<FAScheduleConfig />} />

        {/* HR & Payroll */}
        <Route path="/hr/attendance"       element={<TeacherAttendance />} />
        <Route path="/hr/leave"            element={<LeaveManagement />} />
        <Route path="/hr/salary-structures" element={<SalaryStructures />} />
        <Route path="/hr/salary-slips"     element={<SalarySlips />} />

        {/* Settings */}
        <Route path="/settings/users" element={<UserManagement />} />
        <Route path="/settings/student-import" element={<StudentImport />} />
        <Route path="/settings/academic" element={<AcademicSettings />} />
        <Route path="/settings/teacher-mapping" element={<TeacherMapping />} />

        {/* Reports */}
        <Route path="/whatsapp/inbox"         element={<WhatsAppInbox />} />
        <Route path="/whatsapp/conversations"  element={<WhatsAppConversations />} />
        <Route path="/whatsapp/config"         element={<WhatsAppConfig />} />

        <Route path="/reports" element={<Overview />} />
        <Route path="/reports/attendance" element={<AttendanceReports />} />
        <Route path="/reports/financial" element={<Overview />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default AppRoot;
